import { useEffect, useState } from "react";
import { enablePush, pushState, pushSupport } from "../lib/push";
import { BellIcon, CloseIcon } from "./Icons";
import { notifications } from "@pagemates/shared/copy";

const txt = notifications.pushAsk;

/*
 * Asking for push permission at the moment it's obviously useful (PLAN.md
 * §Notifications): after a first RSVP, after asking people ahead, or when an
 * owner or admin first sees someone asking to join. One soft ask; "Not now"
 * means the app never asks again (Settings still offers it). On iOS in a
 * browser tab, where a permission prompt can't work, a one-time hint to add
 * the app to the home screen instead.
 */

export type PushMoment = "rsvp" | "question" | "requests";
const COPY: Record<PushMoment, { title: string; line: string }> = {
  rsvp: { title: txt.getAReminderThe, line: txt.pagematesCanTellYou },
  question: { title: txt.wantToKnowWhen, line: txt.youLlHearWho },
  requests: { title: txt.hearWhenPeopleAsk, line: txt.soNobodyWaitsLong },
};

const KEY = "pm_push_ask"; // "dismissed" once someone says not now, or turns it on
const IOS_KEY = "pm_push_ios_hint";
const get = (k: string) => { try { return localStorage.getItem(k); } catch { return "dismissed"; } };
const set = (k: string, v: string) => { try { localStorage.setItem(k, v); } catch { /* fine */ } };

let listener: ((m: PushMoment) => void) | null = null;
/** Call right after the moment happens. Does nothing unless an ask is due. */
export function askForPush(moment: PushMoment) { listener?.(moment); }

export function PushAskHost() {
  const [ask, setAsk] = useState<{ moment: PushMoment; ios: boolean } | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    listener = (moment) => {
      const support = pushSupport();
      if (support === "ios-install") { if (!get(IOS_KEY)) setAsk({ moment, ios: true }); return; }
      if (support !== "ok" || get(KEY) || Notification.permission !== "default") return;
      void pushState().then((s) => { if (s === "off") setAsk({ moment, ios: false }); });
    };
    return () => { listener = null; };
  }, []);
  if (!ask) return null;
  const close = () => { set(ask.ios ? IOS_KEY : KEY, "dismissed"); setAsk(null); };
  return (
    <div className="push-ask" role="dialog" aria-label={txt.notifications}>
      <span className="pa-ico" aria-hidden="true"><BellIcon width={20} height={20} /></span>
      <div className="pa-text">
        {ask.ios ? (
          <><b>{txt.addPagematesToYour}</b><span>{txt.inSafariTapShare}</span></>
        ) : (
          <><b>{COPY[ask.moment].title}</b><span>{COPY[ask.moment].line}</span></>
        )}
        <div className="row" style={{ gap: 8, marginTop: 8 }}>
          {ask.ios ? <button type="button" className="btn sm" onClick={close}>{txt.gotIt}</button> : <>
            <button type="button" className="btn primary sm" disabled={busy} onClick={() => { setBusy(true); void enablePush().finally(() => { setBusy(false); close(); }); }}>{txt.turnOnNotifications}</button>
            <button type="button" className="btn ghost sm" onClick={close}>{txt.notNow}</button>
          </>}
        </div>
      </div>
      <button type="button" className="pa-x" aria-label={txt.close} onClick={close}><CloseIcon width={18} height={18} /></button>
    </div>
  );
}
